import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
  "18:00", "18:30", "19:00",
];

function toIso(d) {
  return d.toISOString().slice(0, 10);
}

/** Book a Demo section — landing page */
export default function BookDemoSection() {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d.toISOString().slice(0, 10);
  }, []);

  const [form, setForm] = useState({
    venue_name: "",
    contact_name: "",
    phone: "",
    email: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.venue_name.trim() &&
    form.contact_name.trim() &&
    form.phone.trim().length >= 5 &&
    /.+@.+\..+/.test(form.email) &&
    form.preferred_date &&
    form.preferred_time;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-bookings`, {
        venue_name: form.venue_name.trim(),
        contact_name: form.contact_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        notes: form.notes.trim() || null,
      });
      setDone(true);
      toast.success("Demo request sent — we'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send request. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book-demo"
      data-testid="book-demo-section"
      className="relative z-[2] border-b border-white/10 scroll-mt-24"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
          {/* Left: pitch */}
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
              Book a demo
            </div>
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9]">
              See It.{" "}
              <span className="italic font-normal font-serif text-[#D9A6F5]">Believe It.</span>
              <br />
              Demo It.
            </h2>
            <p className="mt-8 text-lg md:text-xl text-[#A3A3A3] leading-relaxed max-w-md">
              Pick a slot that suits you. We&apos;ll walk you through the platform live, plug in
              your numbers, and show you exactly how much extra you&apos;ll bank each month.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#A3A3A3]">
              {[
                "No sales pitch, just a real walkthrough.",
                "Straight, honest answers to every question.",
                "All confidential and private.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 border border-[#D9A6F5] items-center justify-center text-[#D9A6F5]">
                    <CheckCircle2 size={11} strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form */}
          {done ? (
            <div
              data-testid="book-demo-done"
              className="bg-[#0A0A0A] border border-white/10 p-10 md:p-12 text-center"
            >
              <CheckCircle2 size={44} className="text-[#D9A6F5] mx-auto mb-6" />
              <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
                Request received
              </div>
              <h3 className="text-3xl md:text-4xl font-black tracking-tighter">
                You&apos;re on the list.
              </h3>
              <p className="mt-4 text-[#A3A3A3]">
                We&apos;ve emailed you a confirmation. Keep an eye on your inbox — we&apos;ll be
                in touch shortly to lock in your slot.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="book-demo-form"
              className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Venue name">
                  <input
                    required
                    type="text"
                    value={form.venue_name}
                    onChange={(e) => setField("venue_name", e.target.value)}
                    data-testid="book-demo-venue-name"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Contact name">
                  <input
                    required
                    type="text"
                    value={form.contact_name}
                    onChange={(e) => setField("contact_name", e.target.value)}
                    data-testid="book-demo-contact-name"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Phone number">
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="book-demo-phone"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Email address">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="book-demo-email"
                    className={inputCls}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Preferred day / date" icon={<CalendarDays size={12} />}>
                  <input
                    required
                    type="date"
                    min={toIso(today)}
                    max={maxDate}
                    value={form.preferred_date}
                    onChange={(e) => setField("preferred_date", e.target.value)}
                    data-testid="book-demo-date"
                    className={inputCls}
                  />
                </FormField>
                <FormField label="Preferred time" icon={<Clock size={12} />}>
                  <select
                    required
                    value={form.preferred_time}
                    onChange={(e) => setField("preferred_time", e.target.value)}
                    data-testid="book-demo-time"
                    className={inputCls}
                  >
                    <option value="">Select a time</option>
                    {TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>

              <FormField label="Anything else? (optional)">
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                  data-testid="book-demo-notes"
                  className={`${inputCls} h-auto py-3 resize-y`}
                  placeholder="Tell us a bit about your venue or what you'd like to see"
                />
              </FormField>

              <button
                type="submit"
                disabled={!valid || submitting}
                data-testid="book-demo-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:bg-white/10 disabled:text-[#666] disabled:cursor-not-allowed px-6 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Book my demo <ArrowUpRight size={14} />
                  </>
                )}
              </button>

              <p className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] text-center">
                We&apos;ll confirm your slot within 24 hours.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]";

function FormField({ label, icon, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
        {icon} {label}
      </div>
      {children}
    </label>
  );
}
